import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Briefcase, MapPin, Clock, ArrowRight, Search, Users, TrendingUp, BookOpen,
  Heart, Rocket, Award, Target, Mail, Loader2, Building2, Star, IndianRupee,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import API_BASE from '@/lib/API_BASE';

// Perk icon keys are stored in the DB (admin picks from a fixed list), so the
// mapping lives here and any unknown key falls back rather than crashing.
const PERK_ICONS = {
  growth: TrendingUp, team: Users, flexible: Briefcase, learning: BookOpen,
  health: Heart, impact: Target, rocket: Rocket, award: Award,
};

export const JOB_TYPES = {
  full_time: 'Full-time', part_time: 'Part-time', contract: 'Contract',
  internship: 'Internship', freelance: 'Freelance',
};
export const WORK_MODES = { onsite: 'On-site', remote: 'Remote', hybrid: 'Hybrid' };

/** Shared listing card — also used on the job detail page for related roles. */
export function JobCard({ job, index = 0, compact = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: Math.min(index * 0.05, 0.3) }}
    >
      <Link
        to={`/careers/${job.slug}`}
        className="group block bg-card rounded-2xl border p-5 sm:p-6 hover:border-primary/60 hover:shadow-lg transition-all"
      >
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h3 className="font-heading font-semibold text-lg group-hover:text-primary transition-colors">
                {job.title}
              </h3>
              {job.is_featured && (
                <Badge className="bg-primary/15 text-primary hover:bg-primary/15 border-0 gap-1">
                  <Star className="w-3 h-3" /> Featured
                </Badge>
              )}
              <Badge variant="secondary">{job.department}</Badge>
            </div>

            {job.short_description && !compact && (
              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{job.short_description}</p>
            )}

            <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground">
              {job.location && (
                <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{job.location}</span>
              )}
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {JOB_TYPES[job.job_type] || 'Full-time'} · {WORK_MODES[job.work_mode] || 'On-site'}
              </span>
              {job.experience && (
                <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" />{job.experience}</span>
              )}
              {job.salary_range && (
                <span className="flex items-center gap-1"><IndianRupee className="w-3.5 h-3.5" />{job.salary_range}</span>
              )}
              {job.openings > 1 && (
                <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{job.openings} openings</span>
              )}
            </div>
          </div>

          <span className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:gap-2.5 transition-all">
            View role <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
}

export default function Careers() {
  const [jobs, setJobs]     = useState([]);
  const [page, setPage]     = useState(null);
  const [facets, setFacets] = useState({ departments: [], locations: [], types: [] });
  const [loading, setLoading] = useState(true);
  const [failed, setFailed]   = useState(false);

  const [search, setSearch] = useState('');
  const [dept, setDept]     = useState('all');
  const [type, setType]     = useState('all');

  useEffect(() => {
    let cancelled = false;
    window.scrollTo(0, 0);

    axios.get(`${API_BASE}/careers.php`, { params: { action: 'list' } })
      .then((res) => {
        if (cancelled) return;
        if (res.data?.success) {
          setJobs(res.data.data || []);
          setPage(res.data.page || null);
          setFacets({
            departments: res.data.departments || [],
            locations: res.data.locations || [],
            types: res.data.types || [],
          });
        } else {
          setFailed(true);
        }
      })
      .catch(() => { if (!cancelled) setFailed(true); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => jobs.filter((j) => {
    if (dept !== 'all' && j.department !== dept) return false;
    if (type !== 'all' && j.job_type !== type) return false;
    if (!q) return true;
    return [j.title, j.department, j.location, j.short_description]
      .some((v) => (v || '').toLowerCase().includes(q));
  }), [jobs, dept, type, q]);

  const perks = page?.perks || [];
  const contactEmail = page?.contact_email || 'careers@xceededge.com';
  const filtersOn = dept !== 'all' || type !== 'all' || !!q;

  return (
    <div className="min-h-screen bg-background">
      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-primary/10 via-background to-accent/5">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            {page?.hero_badge && (
              <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary text-sm font-semibold rounded-full mb-4">
                {page.hero_badge}
              </span>
            )}
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              {page?.hero_title || "Build India's Tech Future With Us"}
            </h1>
            {page?.hero_subtitle && (
              <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-8">{page.hero_subtitle}</p>
            )}
            <div className="max-w-md mx-auto relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                className="pl-10 h-12"
                placeholder="Search roles..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Perks ───────────────────────────────────────────────────────── */}
      {perks.length > 0 && (
        <section className="py-12 bg-card border-y">
          <div className="max-w-5xl mx-auto px-4 grid sm:grid-cols-2 md:grid-cols-4 gap-6">
            {perks.map((perk, i) => {
              const Icon = PERK_ICONS[perk.icon] || Award;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className="text-center"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading font-semibold mb-1">{perk.title}</h3>
                  {perk.desc && <p className="text-sm text-muted-foreground">{perk.desc}</p>}
                </motion.div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Openings ────────────────────────────────────────────────────── */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
            <h2 className="font-heading text-2xl font-bold">Open Positions</h2>
            {!loading && (
              <span className="text-sm text-muted-foreground">
                {filtered.length} {filtered.length === 1 ? 'role' : 'roles'}
                {filtersOn && jobs.length !== filtered.length ? ` of ${jobs.length}` : ''} available
              </span>
            )}
          </div>

          {/* Facet filters — only rendered when there is something to filter by */}
          {!loading && jobs.length > 0 && (facets.departments.length > 1 || facets.types.length > 1) && (
            <div className="flex flex-wrap items-center gap-2 mb-6">
              {facets.departments.length > 1 && (
                <select
                  value={dept} onChange={(e) => setDept(e.target.value)}
                  className="h-10 rounded-lg border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  <option value="all">All departments</option>
                  {facets.departments.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              )}
              {facets.types.length > 1 && (
                <select
                  value={type} onChange={(e) => setType(e.target.value)}
                  className="h-10 rounded-lg border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  <option value="all">All types</option>
                  {facets.types.map((t) => <option key={t} value={t}>{JOB_TYPES[t] || t}</option>)}
                </select>
              )}
              {filtersOn && (
                <button
                  onClick={() => { setDept('all'); setType('all'); setSearch(''); }}
                  className="h-10 px-3 rounded-lg border text-sm font-medium text-muted-foreground hover:text-foreground transition"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading open roles…
            </div>
          ) : failed ? (
            <EmptyBlock
              title="We couldn't load our openings"
              text="Please refresh the page, or email us directly and we'll take it from there."
              email={contactEmail}
            />
          ) : filtered.length === 0 ? (
            <EmptyBlock
              title={jobs.length === 0 ? 'No open positions right now' : 'No roles match your search'}
              text={jobs.length === 0
                ? "We're not hiring at the moment, but we're always glad to hear from great people. Send us your resume and we'll reach out when something opens up."
                : 'Try a different search term or clear the filters to see every role.'}
              email={jobs.length === 0 ? contactEmail : null}
              action={jobs.length > 0 ? (
                <button
                  onClick={() => { setDept('all'); setType('all'); setSearch(''); }}
                  className="mt-5 inline-flex items-center h-11 px-5 rounded-lg bg-primary text-primary-foreground font-semibold hover:opacity-90 transition"
                >
                  Show all roles
                </button>
              ) : null}
            />
          ) : (
            <div className="space-y-4">
              {filtered.map((job, i) => <JobCard key={job.id} job={job} index={i} />)}
            </div>
          )}
        </div>
      </section>

      {/* ── Culture ─────────────────────────────────────────────────────── */}
      {page?.culture_title && (
        <section className="py-16 bg-card border-t">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-6 h-6 text-primary" />
            </div>
            <h2 className="font-heading text-2xl font-bold mb-3">{page.culture_title}</h2>
            {page.culture_text && (
              <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{page.culture_text}</p>
            )}
          </div>
        </section>
      )}

      {/* ── Fallback contact ────────────────────────────────────────────── */}
      <section className="py-14 border-t">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h3 className="font-heading text-xl font-bold mb-2">Don't see the right role?</h3>
          <p className="text-muted-foreground mb-5">
            We're always interested in talented people. Send us your resume and tell us what you'd like to work on.
          </p>
          <a
            href={`mailto:${contactEmail}`}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-lg border font-semibold hover:border-primary hover:text-primary transition"
          >
            <Mail className="w-4 h-4" /> {contactEmail}
          </a>
        </div>
      </section>
    </div>
  );
}

function EmptyBlock({ title, text, email, action }) {
  return (
    <div className="text-center py-16 bg-card rounded-2xl border">
      <div className="w-14 h-14 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
        <Briefcase className="w-6 h-6 text-muted-foreground" />
      </div>
      <h3 className="font-heading text-lg font-bold mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground max-w-md mx-auto">{text}</p>
      {email && (
        <a
          href={`mailto:${email}`}
          className="mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-lg border font-semibold hover:border-primary hover:text-primary transition"
        >
          <Mail className="w-4 h-4" /> {email}
        </a>
      )}
      {action}
    </div>
  );
}
